import React from "react";
import "../../styles/experience.css";

const EducationSection = ({ studentData, editableData, isEditing, onInputChange }) => {
  const currentYear = new Date().getFullYear();

  const renderInput = (key, type = "text", placeholder = "-", readOnly = false, extraProps = {}) => {
    const value = editableData[key] ?? studentData.courseDetails?.[key] ?? "";
    if (!isEditing) return studentData.courseDetails?.[key] || "";

    return (
      <input
        type={type}
        value={value}
        onChange={(e) => onInputChange(key, e.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
        style={readOnly ? { backgroundColor: "#f5f5f5", cursor: "not-allowed" } : {}}
        {...extraProps}
      />
    );
  };

  /* -------------------- EXPERIENCE HANDLERS --------------------- */
  const handleExperienceTableChange = (index, field, value) => {
    const updated = [...(editableData.experienceDetails || [{}, {}, {}])];
    updated[index][field] = value;
    onInputChange("experienceDetails", updated);
  };
  
  const removeExperienceRow = (index) => {
    const rows = [...(editableData.experienceDetails || [])];
    rows.splice(index, 1);
    onInputChange("experienceDetails", rows);
  };

  const addExperienceRow = () => {
    const rows = editableData.experienceDetails || [];
    if (rows.length >= 3) return;

    const updated = [...rows, { companyName: "", startDate: "", endDate: "", role: "", technologies: "" }];
    onInputChange("experienceDetails", updated);
  };

  // ✅ Get experience details for view mode from studentData
  const getViewModeExperienceDetails = () => {
    const cd = studentData.courseDetails || {};
    const details = [
      {
        companyName: cd.expCompanyName1 || "",
        startDate: cd.expStartDate1 || "",
        endDate: cd.expEndDate1 || "",
        role: cd.expRole1 || "",
        technologies: cd.expTechnologies1 || "",
        currentlyWorking: cd.expCurrentlyWorking1 || false
      },
      {
        companyName: cd.expCompanyName2 || "",
        startDate: cd.expStartDate2 || "",
        endDate: cd.expEndDate2 || "",
        role: cd.expRole2 || "",
        technologies: cd.expTechnologies2 || "",
        currentlyWorking: cd.expCurrentlyWorking2 || false
      },
      {
        companyName: cd.expCompanyName3 || "",
        startDate: cd.expStartDate3 || "",
        endDate: cd.expEndDate3 || "",
        role: cd.expRole3 || "",
        technologies: cd.expTechnologies3 || "",
        currentlyWorking: cd.expCurrentlyWorking3 || false
      }
    ];

    // Only return rows that have at least one field filled
    return details.filter(row => 
      row.companyName || row.role || row.technologies || row.startDate || row.endDate
    );
  };

  // ✅ Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  // ✅ Determine current experience value
  const currentExperience = isEditing 
    ? (editableData.experience || "") 
    : (studentData.courseDetails?.experience || "");

  const viewModeExperienceDetails = getViewModeExperienceDetails();
  const shouldShowExperienceTable = currentExperience && currentExperience !== "Fresher";

  return (
    <div>
      {/* ====================== EDUCATION TABLE (Existing) ====================== */}
      <div className="education-table">
        <table>
          <thead>
            <tr>
              <th>Education Level</th>
              <th>Degree</th>
              <th>Department</th>
              <th>Institution Name and Address</th>
              <th>Passed Out Year</th>
              <th>Percentage</th>
            </tr>
          </thead>
          <tbody>
            {/* ---------- 10th ---------- */}
            <tr>
              <td>10th</td>
              <td>{renderInput("tenthDegree", "text", "-", true)}</td>
              <td>{renderInput("tenthDepartment", "text", "-", true)}</td>
              <td>{renderInput("tenthInstitution", "text", "Enter institution name")}</td>
              <td>{renderInput("tenthPassout", "number", "YYYY", false, { min: 1990, max: currentYear })}</td>
              <td>{renderInput("tenthPercentage", "number", "%", false, { min: 0, max: 100, step: 0.01 })}</td>
            </tr>

            {/* ---------- 12th ---------- */}
            <tr>
              <td>12th</td>
              <td>{renderInput("twelfthDegree", "text", "-", true)}</td>
              <td>{renderInput("twelfthDepartment", "text", "-", true)}</td>
              <td>{renderInput("twelfthInstitution", "text", "Enter institution name")}</td>
              <td>{renderInput("twelfthPassout", "number", "YYYY", false, { min: 1990, max: currentYear })}</td>
              <td>{renderInput("twelfthPercentage", "number", "%", false, { min: 0, max: 100, step: 0.01 })}</td>
            </tr>

            {/* ---------- Diploma ---------- */}
            <tr>
              <td>Diploma</td>
<td>
  {isEditing ? (
    <select
      value={editableData.diplomaDegree || studentData.courseDetails?.diplomaDegree || ""}
      onChange={(e) => onInputChange("diplomaDegree", e.target.value)}
    >
      <option value="">Select Degree</option>
      <option value="Diploma">Diploma</option>
    </select>
  ) : (
    studentData.courseDetails?.diplomaDegree || ""
  )}
</td>            <td>{renderInput("diplomaDepartment", "text", "Enter department")}</td>
              <td>{renderInput("diplomaInstitution", "text", "Enter institution name")}</td>
              <td>{renderInput("diplomaPassout", "number", "YYYY", false, { min: 1990, max: currentYear })}</td>
              <td>{renderInput("diplomaPercentage", "number", "%", false, { min: 0, max: 100, step: 0.01 })}</td>
            </tr>

            {/* ---------- UG ---------- */}
            <tr>
              <td>UG</td>
              <td>
                {isEditing ? (
                  editableData.ugDegreeDropdown === "Other" ? (
                    renderInput("ugDegree", "text", "Enter your degree")
                  ) : (
                    <select
                      value={editableData.ugDegreeDropdown || studentData.courseDetails?.ugDegree || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "Other") {
                          onInputChange("ugDegreeDropdown", "Other");
                          onInputChange("ugDegree", "");
                        } else {
                          onInputChange("ugDegreeDropdown", value);
                          onInputChange("ugDegree", value);
                        }
                      }}
                    >
                      <option value="">Select Degree</option>
                      <option value="B.Tech">B.Tech</option>
                      <option value="B.E">B.E</option>
                      <option value="B.Sc">B.Sc</option>
                      <option value="B.Com">B.Com</option>
                      <option value="B.A">B.A</option>
                      <option value="Other">Other</option>
                    </select>
                  )
                ) : (
                  studentData.courseDetails?.ugDegree || editableData.ugDegree || ""
                )}
              </td>

              <td>
                {isEditing ? (
                  editableData.ugDepartmentIsOther ? (
                    renderInput("ugDepartment", "text", "Enter your department")
                  ) : (
                    <select
                      value={editableData.ugDepartment || studentData.courseDetails?.ugDepartment || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "Other") {
                          onInputChange("ugDepartment", "");
                          onInputChange("ugDepartmentIsOther", true);
                        } else {
                          onInputChange("ugDepartment", value);
                        }
                      }}
                    >
                      <option value="">Select Department</option>
                      <option value="CSE">CSE</option>
                      <option value="IT">IT</option>
                      <option value="Mechanical">Mechanical</option>
                      <option value="ECE">ECE</option>
                      <option value="EEE">EEE</option>
                      <option value="Civil">Civil</option>
                      <option value="Other">Other</option>
                    </select>
                  )
                ) : (
                  studentData.courseDetails?.ugDepartment || ""
                )}
              </td>

              <td>{renderInput("ugInstitution", "text", "Enter institution name")}</td>
              <td>{renderInput("ugPassout", "number", "YYYY", false, { min: 1990, max: currentYear + 10 })}</td>
              <td>{renderInput("ugPercentage", "number", "CGPA or %", false, { min: 0, max: 100, step: 0.01 })}</td>
            </tr>

            {/* ---------- PG ---------- */}
            <tr>
              <td>PG</td>
              <td>
                {isEditing ? (
                  editableData.pgDegreeIsOther ? (
                    renderInput("pgDegree", "text", "Enter your degree")
                  ) : (
                    <select
                      value={editableData.pgDegree || studentData.courseDetails?.pgDegree || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "Other") {
                          onInputChange("pgDegree", "");
                          onInputChange("pgDegreeIsOther", true);
                        } else {
                          onInputChange("pgDegree", value);
                        }
                      }}
                    >
                      <option value="">Select Degree</option>
                      <option value="M.S">M.S</option>
                      <option value="M.Tech">M.Tech</option>
                      <option value="MBA">MBA</option>
                      <option value="Other">Other</option>
                    </select>
                  )
                ) : (
                  studentData.courseDetails?.pgDegree || ""
                )}
              </td>

              <td>
                {isEditing ? (
                  editableData.pgDepartmentIsOther ? (
                    renderInput("pgDepartment", "text", "Enter your department")
                  ) : (
                    <select
                      value={editableData.pgDepartment || studentData.courseDetails?.pgDepartment || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "Other") {
                          onInputChange("pgDepartment", "");
                          onInputChange("pgDepartmentIsOther", true);
                        } else {
                          onInputChange("pgDepartment", value);
                        }
                      }}
                    >
                      <option value="">Select Department</option>
                      <option value="CSE">CSE</option>
                      <option value="IT">IT</option>
                      <option value="Mechanical">Mechanical</option>
                      <option value="ECE">ECE</option>
                      <option value="EEE">EEE</option>
                      <option value="Civil">Civil</option>
                      <option value="Other">Other</option>
                    </select>
                  )
                ) : (
                  studentData.courseDetails?.pgDepartment || ""
                )}
              </td>

              <td>{renderInput("pgInstitution", "text", "Enter institution name")}</td>
              <td>{renderInput("pgPassout", "number", "YYYY", false, { min: 1990, max: currentYear + 10 })}</td>
              <td>{renderInput("pgPercentage", "number", "CGPA or %", false, { min: 0, max: 100, step: 0.01 })}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ====================== EXPERIENCE SECTION ====================== */}
      <div className="experience-section">
        <label>Work Experience :</label>

        {isEditing ? (
          <select
            className="experience-dropdown"
            value={editableData.experience || ""}
            onChange={(e) => onInputChange("experience", e.target.value)}
          >
            <option value="">Select Experience</option>
            <option value="Fresher">Fresher</option>
            <option value="<1year">&lt; 1 year</option>
            <option value="1-2years">1 - 2 years</option>
            <option value="2-3years">2 - 3 years</option>
            <option value=">3years">&gt; 3 years</option>
          </select>
        ) : (
          <p className="experience-readonly-value">
            {currentExperience || "Not Provided"}
          </p>
        )}

        {/* ✅ SHOW EXPERIENCE TABLE IN BOTH EDIT AND VIEW MODE */}
        {shouldShowExperienceTable && (
          <div className="experience-table-container">
            <table className="experience-table">
              <thead>
                <tr>
                  <th>Company Name</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Role</th>
                  <th>Technologies</th>
                  {isEditing && <th>Action</th>}
                </tr>
              </thead>

              <tbody>
                {isEditing ? (
                  /* ✅ EDIT MODE - Show editable rows */
                  (editableData.experienceDetails || []).map((row, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="text"
                          value={row.companyName || ""}
                          onChange={(e) =>
                            handleExperienceTableChange(index, "companyName", e.target.value)
                          }
                        />
                      </td>

                      <td>
                        <input
                          type="date"
                          value={row.startDate || ""}
                          onChange={(e) =>
                            handleExperienceTableChange(index, "startDate", e.target.value)
                          }
                        />
                      </td>

                      <td className="enddate-cell">
                        {row.currentlyWorking ? (
                          <input type="text" value="Present" disabled />
                        ) : (
                          <input
                            type="date"
                            value={row.endDate || ""}
                            onChange={(e) =>
                              handleExperienceTableChange(index, "endDate", e.target.value)
                            }
                          />
                        )}

                        <div className="current-checkbox">
                          <input
                            type="checkbox"
                            checked={row.currentlyWorking || false}
                            onChange={(e) => {
                              handleExperienceTableChange(index, "currentlyWorking", e.target.checked);
                              if (e.target.checked) {
                                handleExperienceTableChange(index, "endDate", "");
                              }
                            }}
                          />
                          <label>Currently Working</label>
                        </div>
                      </td>

                      <td>
                        <input
                          type="text"
                          value={row.role || ""}
                          onChange={(e) =>
                            handleExperienceTableChange(index, "role", e.target.value)
                          }
                        />
                      </td>

                      <td>
                        <input
                          type="text"
                          value={row.technologies || ""}
                          onChange={(e) =>
                            handleExperienceTableChange(index, "technologies", e.target.value)
                          }
                        />
                      </td>

                      <td className="delete-row-cell">
                        <button
                          className="delete-row-btn"
                          onClick={() => removeExperienceRow(index)}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  /* ✅ VIEW MODE - Show readonly rows with saved data */
                  viewModeExperienceDetails.map((row, index) => (
                    <tr key={index}>
                      <td>{row.companyName || "-"}</td>
                      <td>{formatDate(row.startDate)}</td>
                      <td>{row.currentlyWorking ? "Present" : formatDate(row.endDate)}</td>
                      <td>{row.role || "-"}</td>
                      <td>{row.technologies || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* ✅ Add button (only in edit mode, max 3 rows) */}
            {isEditing && (editableData.experienceDetails?.length || 0) < 3 && (
              <button className="add-row-btn" onClick={addExperienceRow}>
                + Add Row
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EducationSection;